import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { MatDateRangePicker } from '@angular/material/datepicker';
import { Platform } from '@angular/cdk/platform';

import { DurationListComponent } from '../duration-list/duration-list.component';
import { BREAKPOINTS_SMALL, MAX_SELECTABLE_DATE_RANGE_IN_DAYS } from '@app-core/constants/constants';
import { DateService } from '@app-core/services/date/date.service';
import { DurationRangeService } from '@app-core/services/duration-range/duration-range.service';
import { Days, DurationRange } from '@app-core/models/core.model';
import { DataService } from '@app-core/services/data/data.service';

@Component({
  selector: 'app-duration-range',
  templateUrl: './duration-range.component.html',
  styleUrls: ['./duration-range.component.scss'],
})
export class DurationRangeComponent implements OnInit, OnChanges {
  public maxStartDate: string;
  public maxEndDate: string;
  public minStartDate: string;
  public form: FormGroup;
  public isTouchDevice = false;
  public selectedDuration: Days;
  public selectedLabel: string = '';
  public customDurationRangeSettings = null;
  private componentRef;
  private now: Date;
  private maxDateSet: boolean = false;

  private readonly DATA_RETENTION_PERIOD = this.durationService.dataRetentionDays;
  private readonly MAX_SELECTABLE_DATE_RANGE_IN_DAYS = Math.min(this.DATA_RETENTION_PERIOD, MAX_SELECTABLE_DATE_RANGE_IN_DAYS);

  @Input() public filterType: 'flat' | 'box' = 'flat';
  @Input() public durationRange: DurationRange;
  @Input() public removeLabel: boolean = false;
  @Input() public isOutlined: boolean = false;
  @Input() public isWideFeild: boolean = false;
  @Input() public removeCustomOption: boolean = false;
  @Input() public isReactMicroFrontend: boolean;
  @Input() runtime?: 'react';

  @HostBinding('class.react-mf') get reactMfClass() {
    return (this.isReactMicroFrontend = this.runtime === 'react');
  }

  @Input()
  set customSettings(value) {
    if (!value) {
      this.customDurationRangeSettings = null;
      return;
    }
    let { customDurationList = null, dataRetentionPeriod = 0, maxSelectableDateRange = 0 } = value;
    dataRetentionPeriod = Math.min(dataRetentionPeriod, this.DATA_RETENTION_PERIOD);
    maxSelectableDateRange = Math.min(dataRetentionPeriod, maxSelectableDateRange);
    const verifiedList = this.durationService.getCustomDurationList(customDurationList, dataRetentionPeriod);
    this.customDurationRangeSettings = {
      customDurationList: verifiedList,
      dataRetentionPeriod,
      maxSelectableDateRange,
    };
  }

  @Output() durationChange: EventEmitter<any> = new EventEmitter();

  @ViewChild('picker') private picker: MatDateRangePicker<any>;

  constructor(
    private dateService: DateService,
    public dataService: DataService,
    private durationService: DurationRangeService,
    private viewContainerRef: ViewContainerRef,
    private platform: Platform,
    private breakpointObserver: BreakpointObserver
  ) {}

  ngOnInit(): void {
    this.isReactMicroFrontend = this.runtime === 'react';
    const timezone = this.dataService._currentTimeZone.value;
    this.now = this.dateService.getNowInTimezone(timezone);
    this.form = new FormGroup({
      startDate: new FormControl(this.now, [Validators.required]),
      endDate: new FormControl(this.now, [Validators.required, this.endDateValidator()]),
    });

    this.setMinMaxDateRanges();
    this.setDateRange(this.durationRange);

    this.breakpointObserver.observe([...BREAKPOINTS_SMALL]).subscribe((state: BreakpointState) => {
      this.isTouchDevice = state.matches;
    });
    this.isTouchDevice = this.platform.ANDROID || this.platform.IOS;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.durationRange?.currentValue) {
      this.setDateRange(this.durationRange);
    }
  }

  private endDateValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const endDate = control.value;
      const startDate = this.form?.get('startDate').value;
      if (endDate && startDate) {
        const maxDate = new Date(startDate);
        this.customDurationRangeSettings?.maxSelectableDateRange
          ? maxDate.setDate(startDate.getDate() + this.customDurationRangeSettings?.maxSelectableDateRange)
          : maxDate.setDate(startDate.getDate() + MAX_SELECTABLE_DATE_RANGE_IN_DAYS);

        if (endDate.getTime() > maxDate.getTime()) {
          return { endDateInvalid: true };
        }
      }
      return null;
    };
  }

  private setMinMaxDateRanges() {
    this.maxStartDate = this.maxEndDate = this.now.toISOString();
    this.minStartDate = this.customDurationRangeSettings?.dataRetentionPeriod
      ? this.dateService.subtractDays(this.maxStartDate, this.customDurationRangeSettings.dataRetentionPeriod - 1)
      : this.dateService.subtractDays(this.maxStartDate, this.DATA_RETENTION_PERIOD - 1);
  }

  private setMaxDate() {
    const { startDate } = this.form?.value || {};
    if (startDate) {
      const maxDate = new Date(startDate);
      this.customDurationRangeSettings?.maxSelectableDateRange
        ? maxDate.setDate(startDate.getDate() + this.customDurationRangeSettings?.maxSelectableDateRange)
        : maxDate.setDate(startDate.getDate() + this.MAX_SELECTABLE_DATE_RANGE_IN_DAYS);

      this.maxEndDate = maxDate.getTime() > this.now.getTime() ? this.now.toISOString() : maxDate.toISOString();
    }
  }

  private setDateRange(durationRange: DurationRange) {
    const { days, startDate, endDate } = durationRange;

    if (days && startDate && endDate) {
      this.form?.patchValue({ startDate, endDate });
      this.selectedDuration = days;

      if (this.filterType === 'box') {
        this.updateLabel(days, startDate, endDate);
      }
    }

    this.setMaxDate();
  }

  /** Only used in Box Filter */
  private updateLabel(days: Days, startDate: Date, endDate: Date) {
    if (typeof days === 'number') {
      this.selectedLabel = `Past ${days.toString().padStart(2, '0')} days`;
    } else if (days === 'yesterday') {
      this.selectedLabel = 'Yesterday';
    } else if (days === 'today') {
      this.selectedLabel = 'Today';
    } else if (days === 'custom') {
      this.selectedLabel = `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;
    }
  }

  public onStartDateChange() {
    const { startDate, endDate } = this.form.value;
    this.selectedDuration = 'custom';
    if (this.componentRef) {
      this.componentRef.instance.selectedItem = this.selectedDuration;
    }

    if (startDate && endDate && startDate.getTime() > endDate.getTime()) {
      this.form.patchValue({ endDate: null });
    }
    this.setMaxDate();
    this.maxDateSet = true;

    if (this.filterType === 'box' && startDate && endDate) {
      this.updateLabel('custom', startDate, endDate);
    }
  }

  public onEndDateChange() {
    if (!this.maxDateSet) {
      this.setMaxDate();
      this.maxDateSet = true;
    }
    this.selectedDuration = 'custom';
    if (this.componentRef) {
      this.componentRef.instance.selectedItem = this.selectedDuration;
    }

    const { startDate, endDate } = this.form.value;
    if (this.filterType === 'box' && startDate && endDate) {
      this.updateLabel('custom', startDate, endDate);
    }
  }

  public close() {
    if (!this.form.valid || !this.selectedDuration) return;

    let { startDate, endDate } = this.form.value;

    // if (startDate && !endDate) {
    //   endDate = new Date(startDate);
    //   this.form?.patchValue({ endDate });
    // }

    const now = new Date(this.maxStartDate);
    const value = {
      days: this.selectedDuration,
      startDate: new Date(startDate.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds())),
      endDate: new Date(endDate.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds())),
    };

    if (this.filterType === 'box') {
      // Always respect custom if user picked dates
      if (this.form.get('startDate').dirty || this.form.get('endDate').dirty || this.selectedDuration === 'custom') {
        this.selectedDuration = 'custom';
      }
      this.updateLabel(this.selectedDuration, value.startDate, value.endDate);
    }

    this.durationChange.emit(value);

    this.componentRef?.destroy();
    this.componentRef = null;
    this.maxDateSet = false;
  }

  private determineSelectedDuration(): void {
    const { startDate, endDate } = this.form.value;
    if (!startDate || !endDate) return;

    const today = new Date();
    const diffInDays = Math.round((today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));

    if (this.isToday(startDate) && this.isToday(endDate)) {
      this.selectedDuration = 'today';
    } else if (diffInDays === 2 && this.isToday(endDate)) {
      this.selectedDuration = 3;
    } else if (diffInDays === 6 && this.isToday(endDate)) {
      this.selectedDuration = 7;
    } else if (diffInDays === 13 && this.isToday(endDate)) {
      this.selectedDuration = 14;
    } else if (diffInDays === 29 && this.isToday(endDate)) {
      this.selectedDuration = 30;
    } else {
      this.selectedDuration = 'custom';
    }
  }

  private isToday(date: Date): boolean {
    const today = this.now;
    return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
  }

  public open() {
    if (this.filterType === 'flat') {
      this.determineSelectedDuration();
    }
    this.insertDurationList();
    this.form.valid ? (this.maxEndDate = this.now.toISOString()) : this.setMaxDate();
  }

  public insertDurationList() {
    var datepickerContent = document.querySelector('mat-datepicker-content');
    if (datepickerContent) {
      const componentRef = this.viewContainerRef.createComponent(DurationListComponent);
      if (this.removeCustomOption) {
        componentRef.instance.durationList = componentRef.instance.durationList.filter((item) => item.days !== 'custom');
      }
      this.componentRef = componentRef;

      const contentElement = (componentRef.hostView as any).rootNodes[0] as HTMLElement;
      const matCalendar = datepickerContent.querySelector('.mat-calendar');
      matCalendar.parentElement.style.flexDirection = 'row';

      if (this.isTouchDevice) {
        matCalendar.parentElement.style.height = 'auto';
        const durationList = contentElement.querySelector('.durationListContainer') as HTMLElement;
        if (durationList) {
          durationList.style.width = 'auto';
        }
      }

      matCalendar.parentNode.insertBefore(contentElement, matCalendar);
      componentRef.instance.selectedItem = this.selectedDuration;

      if (this.customDurationRangeSettings?.customDurationList) {
        componentRef.instance.durationList = this.customDurationRangeSettings.customDurationList;
      }

      const subscription = componentRef.instance.durationSelected.subscribe((days) => {
        this.getDateRange(days);
      });

      componentRef.onDestroy(() => {
        subscription.unsubscribe();
      });
    }
  }

  private getDateRange(days: Days): void {
    let startDate, endDate;
    const timezone = this.dataService._currentTimeZone.value;
    if (typeof days === 'number') {
      ({ from: startDate, to: endDate } = this.dateService.getDateRange(days, timezone));
      this.picker.close();
    } else if (days === 'yesterday') {
      ({ from: startDate, to: endDate } = this.dateService.getDateRange(2, timezone));
      endDate.setDate(endDate.getDate() - 1);
      this.picker.close();
    } else if (days === 'today') {
      ({ from: startDate, to: endDate } = this.dateService.getDateRange(1, timezone));
      this.picker.close();
    } else if (days === 'custom') {
      startDate = endDate = this.dateService.getNowInTimezone(timezone);
    }

    this.setDateRange({ days, startDate, endDate });
  }
}
